import { ExperimentalLayer, ConfigValueType, ConfigurationMetaConstraintKind, BucketingEntityType, BucketingStrategy, SwitchbackScope, SwitchbackPreviewWarning, SwitchbackSegmentType } from "../../modules/experiments/types";
import { ConfigurationMetaValidationError } from "../../modules/experiments/errors";
// -- Service → SDK transformations --
export function variantFromService(service) {
    return {
        id: service.id,
        name: service.name,
        weight: service.weight,
        configuration: service.configuration || {},
        isControl: service.is_control,
        createdAt: service.created_at ? new Date(service.created_at) : undefined,
        updatedAt: service.updated_at ? new Date(service.updated_at) : undefined
    };
}
export function eligibilityCriteriaFromService(service) {
    return {
        id: service.id,
        name: service.name,
        minBudget: service.min_budget,
        maxBudget: service.max_budget,
        minDurationDays: service.min_duration_days,
        maxDurationDays: service.max_duration_days,
        minImpressionGoal: service.min_impression_goal,
        maxImpressionGoal: service.max_impression_goal,
        minPacingHorizonPct: service.min_pacing_horizon_pct,
        archived: service.archived,
        allowedPacingModes: service.allowed_pacing_modes,
        allowedBillingTypes: service.allowed_billing_types,
        allowedInventorySources: service.allowed_inventory_sources,
        allowedMediaSubTypes: service.allowed_media_sub_types
    };
}
export function includedEntityFromService(service) {
    return {
        entityId: service.entity_id,
        preallocatedVariant: service.preallocated_variant
    };
}
export function excludedEntityFromService(service) {
    return {
        entityId: service.entity_id,
        excludedByUsr: service.excluded_by_usr
    };
}
export function githubPrReviewFromService(service) {
    return {
        prLink: service.pr_link,
        comments: service.comments
    };
}
const SWITCHBACK_SCOPES = new Set(Object.values(SwitchbackScope));
/** An empty scope means GLOBAL; throws if a nonempty scope isn't a known SwitchbackScope. */
export function parseSwitchbackScope(scope) {
    if (!scope) {
        return SwitchbackScope.GLOBAL;
    }
    if (!SWITCHBACK_SCOPES.has(scope)) {
        throw new ConfigurationMetaValidationError(`Unknown switchback scope: "${scope}"`);
    }
    return scope;
}
export function switchbackConfigFromService(service) {
    if (!service) {
        return undefined;
    }
    return {
        scope: parseSwitchbackScope(service.scope),
        slotMs: service.slot_ms ?? 0,
        gapMs: service.gap_ms ?? 0,
        burnInMs: service.burn_in_ms ?? 0,
        ...(service.epoch && { epoch: new Date(service.epoch) })
    };
}
export function switchbackConfigToService(config) {
    if (!config) {
        return undefined;
    }
    return {
        scope: config.scope,
        slot_ms: config.slotMs,
        gap_ms: config.gapMs,
        burn_in_ms: config.burnInMs ?? 0,
        ...(config.epoch && { epoch: config.epoch.toISOString() })
    };
}
export function experimentFromService(service) {
    return {
        id: service.id,
        name: service.name,
        description: service.description,
        startDate: service.start_date ? new Date(service.start_date) : null,
        endDate: service.end_date ? new Date(service.end_date) : null,
        status: parseExperimentStatus(service.status),
        participationRate: service.participation_rate,
        exposureSampleRate: service.exposure_sample_rate,
        variants: (service.variants || []).map(variantFromService),
        eligibilityCriteriaId: service.eligibility_criteria?.id,
        includedEntities: (service.included_entities || []).map(includedEntityFromService),
        excludedEntities: (service.excluded_entities || []).map(excludedEntityFromService),
        optInOnly: service.opt_in_only,
        sampleSegments: service.sample_segments,
        maxImpressionCount: service.max_impression_count ?? undefined,
        maxExposureCount: service.max_exposure_count ?? undefined,
        bucketingEntityType: service.bucketing_entity_type,
        bucketingStrategy: service.bucketing_strategy,
        switchbackConfig: switchbackConfigFromService(service.switchback_config),
        layers: service.layers,
        createdAt: new Date(service.created_at),
        updatedAt: new Date(service.updated_at),
        createdBy: service.created_by,
        updatedBy: service.updated_by,
        creatorEmail: service.creator_email,
        githubPrReviews: service.github_pr_reviews?.map(githubPrReviewFromService),
        advertiserId: service.advertiser_id || undefined
    };
}
export function layerConfigurationFromService(layer, service) {
    return {
        version: service.version,
        layer: layer,
        configuration: service.configuration || {}
    };
}
const CONFIG_VALUE_TYPES = new Set(Object.values(ConfigValueType));
function parseConfigValueType(valueType) {
    if (!CONFIG_VALUE_TYPES.has(valueType)) {
        throw new ConfigurationMetaValidationError(`Unknown configuration meta value type: "${valueType}"`);
    }
    return valueType;
}
export function configurationMetaVariableFromService(service) {
    return {
        id: service.id,
        key: service.key,
        valueType: parseConfigValueType(service.value_type),
        minValue: service.min_value,
        maxValue: service.max_value,
        permittedValues: service.permitted_values,
        description: service.description,
        allowDecimal: service.allow_decimal,
        allowEmpty: service.allow_empty,
        pattern: service.pattern || undefined,
        parentConfig: service.parent_config ?? undefined,
        parentConfigIdx: service.parent_config_idx ?? undefined,
        createdAt: new Date(service.created_at),
        updatedAt: new Date(service.updated_at)
    };
}
export function configurationMetaTriggerFromService(service) {
    const hasValues = !!service.values && service.values.length > 0;
    if (service.match_any) {
        if (hasValues) {
            throw new ConfigurationMetaValidationError(`Configuration meta trigger for variable "${service.variable_id}" has both matchAny and a non-empty values list`);
        }
        return { variableId: service.variable_id, matchAny: true };
    }
    if (!hasValues) {
        throw new ConfigurationMetaValidationError(`Configuration meta trigger for variable "${service.variable_id}" has neither matchAny nor a non-empty values list`);
    }
    return {
        variableId: service.variable_id,
        values: service.values
    };
}
export function configurationMetaGroupingSetFromService(service) {
    return {
        id: service.id || "",
        trigger: service.trigger ? configurationMetaTriggerFromService(service.trigger) : undefined,
        variables: (service.variables || []).map(configurationMetaVariableFromService),
        groupingSets: (service.grouping_sets || []).map(configurationMetaGroupingSetFromService)
    };
}
const CONFIGURATION_META_CONSTRAINT_KINDS = new Set(Object.values(ConfigurationMetaConstraintKind));
function parseConfigurationMetaConstraintKind(kind) {
    if (!CONFIGURATION_META_CONSTRAINT_KINDS.has(kind)) {
        throw new ConfigurationMetaValidationError(`Unknown configuration meta constraint kind: "${kind}"`);
    }
    return kind;
}
export function configurationMetaConstraintFromService(service) {
    const hasControllingValues = !!service.controlling_values && service.controlling_values.length > 0;
    if (service.controlling_match_any && hasControllingValues) {
        throw new ConfigurationMetaValidationError(`Configuration meta constraint "${service.id}" has both controllingMatchAny and a non-empty controllingValues list`);
    }
    if (!service.controlling_match_any && !hasControllingValues) {
        throw new ConfigurationMetaValidationError(`Configuration meta constraint "${service.id}" has neither controllingMatchAny nor a non-empty controllingValues list`);
    }
    const id = service.id;
    const controllingVariableId = service.controlling_variable_id;
    const targetVariableId = service.target_variable_id;
    const createdAt = new Date(service.created_at);
    const updatedAt = new Date(service.updated_at);
    const kind = parseConfigurationMetaConstraintKind(service.kind);
    if (kind === ConfigurationMetaConstraintKind.FORCE_VALUE) {
        if (service.forced_value === undefined) {
            throw new ConfigurationMetaValidationError(`Configuration meta constraint "${service.id}" has kind FORCE_VALUE but no forcedValue`);
        }
        if (service.controlling_match_any) {
            return {
                id,
                controllingVariableId,
                targetVariableId,
                createdAt,
                updatedAt,
                controllingMatchAny: true,
                kind,
                forcedValue: service.forced_value
            };
        }
        return {
            id,
            controllingVariableId,
            targetVariableId,
            createdAt,
            updatedAt,
            controllingMatchAny: false,
            controllingValues: service.controlling_values,
            kind,
            forcedValue: service.forced_value
        };
    }
    if (service.controlling_match_any) {
        return {
            id,
            controllingVariableId,
            targetVariableId,
            createdAt,
            updatedAt,
            controllingMatchAny: true,
            kind,
            restrictMinValue: service.restrict_min_value,
            restrictMaxValue: service.restrict_max_value,
            restrictMinRelative: service.restrict_min_relative,
            restrictMaxRelative: service.restrict_max_relative,
            restrictPermittedValues: service.restrict_permitted_values
        };
    }
    return {
        id,
        controllingVariableId,
        targetVariableId,
        createdAt,
        updatedAt,
        controllingMatchAny: false,
        controllingValues: service.controlling_values,
        kind,
        restrictMinValue: service.restrict_min_value,
        restrictMaxValue: service.restrict_max_value,
        restrictMinRelative: service.restrict_min_relative,
        restrictMaxRelative: service.restrict_max_relative,
        restrictPermittedValues: service.restrict_permitted_values
    };
}
const EXPERIMENTAL_LAYERS = new Set(Object.values(ExperimentalLayer));
/** Throws if `layer` isn't a known ExperimentalLayer, rather than trusting the wire value. */
export function parseExperimentalLayer(layer) {
    if (!EXPERIMENTAL_LAYERS.has(layer)) {
        throw new ConfigurationMetaValidationError(`Unknown experimental layer: "${layer}"`);
    }
    return layer;
}
const EXPERIMENT_STATUSES = new Set([
    "DRAFT",
    "IN_REVIEW",
    "PUBLISHED",
    "ACTIVE",
    "PAUSED",
    "DRAINING",
    "COMPLETED",
    "TERMINATED",
    "ARCHIVED"
]);
/** Throws if `status` isn't a known ExperimentStatus, rather than trusting the wire value. */
export function parseExperimentStatus(status) {
    if (!EXPERIMENT_STATUSES.has(status)) {
        throw new ConfigurationMetaValidationError(`Unknown experiment status: "${status}"`);
    }
    return status;
}
export function configurationMetaFromService(layer, service) {
    return {
        layer: parseExperimentalLayer(layer),
        groupingSets: (service.grouping_sets || []).map(configurationMetaGroupingSetFromService),
        constraints: (service.constraints || []).map(configurationMetaConstraintFromService)
    };
}
const BUCKETING_ENTITY_TYPES = new Set(Object.values(BucketingEntityType));
/** Throws if `value` isn't a known BucketingEntityType, rather than trusting the wire value. */
export function parseBucketingEntityType(value) {
    if (!BUCKETING_ENTITY_TYPES.has(value)) {
        throw new ConfigurationMetaValidationError(`Unknown bucketing entity type: "${value}"`);
    }
    return value;
}
const BUCKETING_STRATEGIES = new Set(Object.values(BucketingStrategy));
/** Throws if `value` isn't a known BucketingStrategy, rather than trusting the wire value. */
export function parseBucketingStrategy(value) {
    if (!BUCKETING_STRATEGIES.has(value)) {
        throw new ConfigurationMetaValidationError(`Unknown bucketing strategy: "${value}"`);
    }
    return value;
}
export function experimentLayerMetaFromService(service) {
    return {
        id: service.id,
        name: parseExperimentalLayer(service.name),
        displayName: service.display_name,
        permittedEligibilityCriteria: service.permitted_eligibility_criteria || [],
        permittedBucketingEntityTypes: (service.permitted_bucketing_entity_types || []).map(parseBucketingEntityType),
        permittedBucketingStrategies: (service.permitted_bucketing_strategies || []).map(parseBucketingStrategy),
        requiredReviewGroups: service.required_review_groups || [],
        activeDefaultsPr: service.active_defaults_pr,
        reservationPriority: service.reservation_priority,
        minReservationSegments: service.min_reservation_segments,
        maxReservationSegments: service.max_reservation_segments,
        maxTotalSampledSegments: service.max_total_sampled_segments,
        createdAt: new Date(service.created_at),
        updatedAt: new Date(service.updated_at)
    };
}
export function gitHubTeamFromService(service) {
    return {
        slug: service.slug,
        name: service.name
    };
}
export function segmentIntervalFromService(service) {
    return { lo: service.lo, hi: service.hi };
}
export function layerReservationSummaryFromService(service) {
    return {
        experimentId: service.experiment_id,
        experimentName: service.experiment_name,
        experimentStatus: parseExperimentStatus(service.experiment_status),
        bucketingEntityType: parseBucketingEntityType(service.bucketing_entity_type),
        segmentLo: service.segment_lo,
        segmentHi: service.segment_hi,
        ageSeconds: service.age_seconds,
        hasEndDate: service.has_end_date
    };
}
export function layerAvailabilityFromService(service) {
    return {
        layer: parseExperimentalLayer(service.layer),
        currentBucketingEntityType: service.current_bucketing_entity_type
            ? parseBucketingEntityType(service.current_bucketing_entity_type)
            : undefined,
        permittedBucketingEntityTypes: (service.permitted_bucketing_entity_types || []).map(parseBucketingEntityType),
        typeAccepted: service.type_accepted,
        typePermitted: service.type_permitted,
        typeMatchesCurrent: service.type_matches_current,
        freeSegments: service.free_segments,
        freeIntervals: (service.free_intervals || []).map(segmentIntervalFromService),
        largestFreeInterval: service.largest_free_interval
            ? segmentIntervalFromService(service.largest_free_interval)
            : undefined,
        minReservationSegments: service.min_reservation_segments,
        maxReservationSegments: service.max_reservation_segments,
        maxTotalSampledSegments: service.max_total_sampled_segments,
        reservations: (service.reservations || []).map(layerReservationSummaryFromService),
        optInShare: service.opt_in_share,
        forcedShare: service.forced_share,
        allocationCursor: service.allocation_cursor,
        reservationPriority: service.reservation_priority
    };
}
const SWITCHBACK_PREVIEW_WARNINGS = new Set(Object.values(SwitchbackPreviewWarning));
function switchbackHourCoverageFromService(service) {
    return service
        ? {
            expectedCycles: service.expected_cycles ?? 0,
            expectedMs: service.expected_ms ?? 0,
            worstCycles: service.worst_cycles ?? 0,
            worstMs: service.worst_ms ?? 0
        }
        : undefined;
}
/**
 * Warnings are advisory, so one this SDK doesn't know yet is dropped rather than
 * failing the whole preview.
 */
export function switchbackSchedulePreviewFromService(service) {
    const { flight, recommended_timing: timing } = service;
    return {
        cycleDurationMs: service.cycle_duration_ms ?? 0,
        initialBurnInMs: service.initial_burn_in_ms ?? 0,
        arms: (service.arms || []).map((arm) => ({
            variantIndex: arm.variant_index ?? 0,
            variantId: arm.variant_id || undefined,
            slotsPerCycle: arm.slots_per_cycle ?? 0,
            measuredMsPerCycle: arm.measured_ms_per_cycle ?? 0,
            share: arm.share ?? 0,
            flightFullLegs: arm.flight_full_legs ?? 0
        })),
        segments: (service.segments || []).map((segment) => ({
            startOffsetMs: segment.start_offset_ms ?? 0,
            durationMs: segment.duration_ms ?? 0,
            arm: segment.arm ?? undefined,
            slots: segment.slots ?? 0,
            type: segment.type === SwitchbackSegmentType.BURN_IN
                ? SwitchbackSegmentType.BURN_IN
                : SwitchbackSegmentType.LEG_OR_GAP
        })),
        hourCoverage: switchbackHourCoverageFromService(service.hour_coverage),
        recommendedMinLengthMs: service.recommended_min_length_ms ?? undefined,
        dayOfWeekCaveat: service.day_of_week_caveat ?? false,
        flight: flight
            ? {
                flightMs: flight.flight_ms ?? 0,
                fullCycles: flight.full_cycles ?? 0,
                lastFullCycleEndMs: flight.last_full_cycle_end_ms ?? 0,
                expectedCoverageShortfallMs: flight.expected_coverage_shortfall_ms ?? undefined,
                worstCoverageShortfallMs: flight.worst_coverage_shortfall_ms ?? undefined
            }
            : undefined,
        recommendedTiming: timing
            ? {
                slotMs: timing.slot_ms ?? 0,
                gapMs: timing.gap_ms ?? 0,
                burnInMs: timing.burn_in_ms ?? 0,
                cycleDurationMs: timing.cycle_duration_ms ?? 0,
                hourCoverage: switchbackHourCoverageFromService(timing.hour_coverage)
            }
            : undefined,
        warnings: (service.warnings || []).filter((warning) => SWITCHBACK_PREVIEW_WARNINGS.has(warning))
    };
}
// -- SDK → Service transformations --
export function configurationMetaVariableToService(variable) {
    return {
        id: variable.id,
        key: variable.key,
        value_type: variable.valueType,
        min_value: variable.minValue,
        max_value: variable.maxValue,
        permitted_values: variable.permittedValues,
        description: variable.description,
        allow_decimal: variable.allowDecimal,
        allow_empty: variable.allowEmpty,
        pattern: variable.pattern,
        parent_config: variable.parentConfig,
        parent_config_idx: variable.parentConfigIdx
    };
}
export function configurationMetaTriggerToService(trigger) {
    if (trigger.matchAny) {
        return { variable_id: trigger.variableId, match_any: true };
    }
    return { variable_id: trigger.variableId, values: trigger.values };
}
export function configurationMetaConstraintToService(constraint) {
    const base = {
        id: constraint.id,
        controlling_variable_id: constraint.controllingVariableId,
        target_variable_id: constraint.targetVariableId,
        kind: constraint.kind,
        ...(constraint.controllingMatchAny
            ? { controlling_match_any: true }
            : { controlling_values: constraint.controllingValues })
    };
    if (constraint.kind === ConfigurationMetaConstraintKind.FORCE_VALUE) {
        return { ...base, forced_value: constraint.forcedValue };
    }
    return {
        ...base,
        restrict_min_value: constraint.restrictMinValue,
        restrict_max_value: constraint.restrictMaxValue,
        restrict_min_relative: constraint.restrictMinRelative,
        restrict_max_relative: constraint.restrictMaxRelative,
        restrict_permitted_values: constraint.restrictPermittedValues
    };
}
export function experimentLayerMetaToService(meta) {
    return {
        name: meta.name,
        display_name: meta.displayName,
        permitted_eligibility_criteria: meta.permittedEligibilityCriteria,
        permitted_bucketing_entity_types: meta.permittedBucketingEntityTypes,
        permitted_bucketing_strategies: meta.permittedBucketingStrategies,
        required_review_groups: meta.requiredReviewGroups,
        reservation_priority: meta.reservationPriority,
        min_reservation_segments: meta.minReservationSegments,
        max_reservation_segments: meta.maxReservationSegments,
        max_total_sampled_segments: meta.maxTotalSampledSegments
    };
}
export function cleanVariantConfiguration(configuration) {
    const cleaned = {};
    for (const [key, value] of Object.entries(configuration)) {
        if (key.trim() === "" || value === "") {
            continue;
        }
        cleaned[key] = value;
    }
    return cleaned;
}
export function variantToService(variant, referencedVariantIds = new Set()) {
    const cleanedConfig = cleanVariantConfiguration(variant.configuration);
    const service = {
        name: variant.name,
        weight: variant.weight,
        configuration: cleanedConfig,
        is_control: variant.isControl
    };
    if (Object.keys(cleanedConfig).length === 0) {
        delete service.configuration;
    }
    // Forward IDs only for server-persisted variants, or client-generated ones an included entity's
    // preallocated_variant points at; other client UUIDs are UI state and must not reach the API.
    if (variant.id && (variant.createdAt || referencedVariantIds.has(variant.id))) {
        service.id = variant.id;
    }
    if (variant.createdAt)
        service.created_at = variant.createdAt.toISOString();
    if (variant.updatedAt)
        service.updated_at = variant.updatedAt.toISOString();
    return service;
}
export function eligibilityCriteriaToService(criteria) {
    return {
        id: criteria.id,
        name: criteria.name,
        min_budget: criteria.minBudget,
        max_budget: criteria.maxBudget,
        min_duration_days: criteria.minDurationDays,
        max_duration_days: criteria.maxDurationDays,
        min_impression_goal: criteria.minImpressionGoal,
        max_impression_goal: criteria.maxImpressionGoal,
        min_pacing_horizon_pct: criteria.minPacingHorizonPct,
        allowed_pacing_modes: criteria.allowedPacingModes,
        allowed_billing_types: criteria.allowedBillingTypes,
        allowed_inventory_sources: criteria.allowedInventorySources,
        allowed_media_sub_types: criteria.allowedMediaSubTypes
    };
}
export function includedEntityToService(entity) {
    return {
        entity_id: entity.entityId,
        preallocated_variant: entity.preallocatedVariant
    };
}
export function excludedEntityToService(entity) {
    return {
        entity_id: entity.entityId,
        excluded_by_usr: entity.excludedByUsr
    };
}
const KNOWN_ENTITY_TYPES = new Set([
    "CAMPAIGN",
    "LINE_ITEM",
    "ADVERTISER",
    "ORG",
    "USER",
    "UNKNOWN"
]);
export function experimentEntityFromService(service) {
    return {
        entityId: service.entity_id,
        name: service.name,
        entityType: KNOWN_ENTITY_TYPES.has(service.entity_type)
            ? service.entity_type
            : "UNKNOWN",
        error: service.error
    };
}
export function experimentToService(experiment, eligibilityCriteria) {
    const includedEntities = experiment.includedEntities || [];
    const preallocatedVariantIds = new Set(includedEntities.map((entity) => entity.preallocatedVariant).filter(Boolean));
    const service = {
        name: experiment.name,
        description: experiment.description,
        start_date: experiment.startDate?.toISOString() || null,
        end_date: experiment.endDate?.toISOString() || null,
        status: experiment.status,
        // participation_rate is nullable in the DB (unlike exposure_sample_rate,
        // which has a NOT NULL DEFAULT); default a missing value to 0 here to
        // match how the rest of the app already treats it, rather than silently
        // promoting an experiment to full participation on save.
        participation_rate: experiment.participationRate ?? 0,
        exposure_sample_rate: experiment.exposureSampleRate ?? 1.0,
        variants: experiment.variants.map((variant) => variantToService(variant, preallocatedVariantIds)),
        included_entities: includedEntities.map(includedEntityToService),
        excluded_entities: (experiment.excludedEntities || []).map(excludedEntityToService),
        opt_in_only: experiment.optInOnly,
        sample_segments: experiment.sampleSegments,
        max_impression_count: experiment.maxImpressionCount ?? undefined,
        max_exposure_count: experiment.maxExposureCount ?? undefined,
        bucketing_entity_type: experiment.bucketingEntityType,
        bucketing_strategy: experiment.bucketingStrategy,
        switchback_config: switchbackConfigToService(experiment.switchbackConfig),
        creator_email: experiment.creatorEmail,
        layers: experiment.layers,
        advertiser_id: experiment.advertiserId || undefined
    };
    if ("id" in experiment && experiment.id) {
        service.id = experiment.id;
    }
    if (eligibilityCriteria) {
        service.eligibility_criteria = eligibilityCriteriaToService(eligibilityCriteria);
    }
    return service;
}
export function optimizationHoldoutRequestToService(request) {
    return {
        advertiser_id: request.advertiserId,
        line_item_id: request.lineItemId,
        kpi: request.kpi
    };
}
export function endOptimizationHoldoutRequestToService(request) {
    return {
        advertiser_id: request.advertiserId,
        line_item_id: request.lineItemId
    };
}
//# sourceMappingURL=index.js.map